import { captureSessionEpoch, isSessionEpochCurrent } from './sessionEpoch';

/** Subscribe before the first list read, with a bounded HTTP-only fallback. */
export class InitialTaskSync {
  private state?: {
    pending: boolean;
    opened: boolean;
    readStarted: boolean;
    ready: Promise<void>;
    release: () => void;
    timer?: ReturnType<typeof setTimeout>;
  };

  constructor(private readonly timeoutMs = 150) {}

  start(): void {
    this.close();
    let release!: () => void;
    const ready = new Promise<void>(resolve => { release = resolve; });
    const state = { pending: true, opened: false, readStarted: false, ready, release,
      timer: undefined as ReturnType<typeof setTimeout> | undefined };
    this.state = state;
    state.timer = setTimeout(() => this.release(state), this.timeoutMs);
  }

  /** Returns whether opening this stream requires a new authoritative read. */
  connected(): boolean {
    const state = this.state;
    if (!state) return true;
    const mustResync = state.opened || state.readStarted;
    state.opened = true;
    this.release(state);
    return mustResync;
  }

  beforeRead(): Promise<void> | undefined {
    const state = this.state;
    if (!state) return;
    if (!state.pending) { state.readStarted = true; return; }
    return state.ready.then(() => {
      if (this.state === state) state.readStarted = true;
    });
  }

  close(): void {
    if (this.state) this.release(this.state);
    this.state = undefined;
  }

  private release(state: NonNullable<InitialTaskSync['state']>): void {
    state.pending = false;
    if (state.timer !== undefined) clearTimeout(state.timer);
    state.timer = undefined;
    state.release();
  }
}

export const initialTaskSync = new InitialTaskSync();

export async function readAfterInitialTaskSync<T>(read: () => Promise<T>): Promise<T> {
  const epoch = captureSessionEpoch();
  const ready = initialTaskSync.beforeRead();
  if (ready) await ready;
  if (!isSessionEpochCurrent(epoch)) throw new Error('账号已切换');
  return read();
}
